import { ApiError, GENERIC_ERROR_DETAIL } from './ApiError';
import type { ProblemBody } from './ApiError';

/** Value types accepted in a query string; `undefined` and `null` entries are dropped. */
export type QueryValue = string | number | boolean | undefined | null;

/** Per-request options. The body is serialized as JSON unless it is already a `FormData`. */
export interface RequestOptions {
  /** Query-string parameters; empty, null and undefined values are omitted. */
  query?: Record<string, QueryValue>;
  /** Aborts the request — pass the signal the data layer provides. */
  signal?: AbortSignal;
  /** Extra headers merged over the defaults. */
  headers?: Record<string, string>;
}

export interface ApiClientOptions {
  /**
   * Origin of the API. Empty means same-origin, which is the deployment model: the Vite proxy in dev
   * and the AKS ingress in production both serve the SPA and `/api` from the same host, so the
   * httpOnly session cookie is first-party and no CORS is involved (01 §7).
   */
  baseUrl?: string;
  /** Path every route is relative to. */
  basePath?: string;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Typed access to the TMR API. Screens never call `fetch` directly (skill `frontend-architecture`,
 * Regra 3): URL, credentials and error mapping live here, once.
 */
export interface ApiClient {
  get<T>(path: string, options?: RequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  delete<T>(path: string, options?: RequestOptions): Promise<T>;
  /**
   * A POST whose answer is a file. For the one case a plain link cannot cover: the body carries what is
   * being rendered, so there is no URL to point an anchor at.
   */
  postForBlob(path: string, body?: unknown, options?: RequestOptions): Promise<Blob>;
  /**
   * Registers the single reaction to a dead session (401): the app clears local state and returns to
   * the login screen. Handled once here so no screen implements its own redirect — and so a burst of
   * parallel 401s does not fire a redirect loop.
   */
  onUnauthorized(handler: () => void): void;
}

/** The standard members of a problem body; everything else is an extension the endpoint chose to send. */
const STANDARD_PROBLEM_MEMBERS = new Set(['type', 'title', 'status', 'detail', 'instance', 'code', 'errors', 'traceId']);

function extensionsOf(problem: ProblemBody): Record<string, unknown> {
  if (typeof problem !== 'object' || problem === null) return {};
  return Object.fromEntries(
    Object.entries(problem as Record<string, unknown>).filter(([key]) => !STANDARD_PROBLEM_MEMBERS.has(key)),
  );
}

/** Creates a client. Prefer the shared {@link api} instance; this exists for tests and for other origins. */
export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = (options.baseUrl ?? '').replace(/\/+$/, '');
  const basePath = options.basePath ?? '/api/v1';
  let unauthorizedHandler: (() => void) | null = null;
  let unauthorizedNotified = false;

  function buildUrl(path: string, query?: Record<string, QueryValue>): string {
    const url = `${baseUrl}${basePath}${path.startsWith('/') ? path : `/${path}`}`;
    if (!query) return url;

    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === '') continue;
      params.set(key, String(value));
    }
    const qs = params.toString();
    return qs ? `${url}?${qs}` : url;
  }

  async function toError(response: Response): Promise<ApiError> {
    let problem: ProblemBody = {};
    try {
      problem = (await response.json()) as ProblemBody;
    } catch {
      // A failure without a JSON body (proxy error page, network-level 502) still becomes an ApiError.
    }
    return new ApiError(
      response.status,
      problem.code ?? null,
      problem.detail?.trim() || GENERIC_ERROR_DETAIL,
      problem.title ?? null,
      problem.errors ?? {},
      extensionsOf(problem),
    );
  }

  async function request<T>(
    method: Method,
    path: string,
    body?: unknown,
    options: RequestOptions = {},
  ): Promise<T> {
    const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };
    const hasBody = body !== undefined;
    if (hasBody) headers['Content-Type'] = 'application/json';

    const response = await fetch(buildUrl(path, options.query), {
      method,
      // The TMR session is an httpOnly cookie — never a token the browser holds (05 §3).
      credentials: 'include',
      headers,
      body: hasBody ? JSON.stringify(body) : undefined,
      signal: options.signal,
    });

    if (!response.ok) {
      const error = await toError(response);
      // The session died: react once, no matter how many requests failed together.
      if (error.isUnauthorized && !unauthorizedNotified) {
        unauthorizedNotified = true;
        unauthorizedHandler?.();
      }
      throw error;
    }

    unauthorizedNotified = false;

    // 204, and any other empty success, resolve to undefined rather than blowing up on JSON parsing.
    if (response.status === 204 || response.headers.get('Content-Length') === '0') {
      return undefined as T;
    }
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  /**
   * A POST whose answer is a file rather than JSON.
   *
   * It exists because one endpoint genuinely needs it: the template preview prints **unsaved** text, so
   * the text travels in the body and there is no URL an anchor could point at (`24` §9). Every other file
   * the portal serves is a GET, and those stay plain links — the browser downloads them with the session
   * cookie and nothing has to buffer anything.
   *
   * Failures still become an {@link ApiError}, including the 503 an environment with no renderer answers:
   * the caller decides what that means on screen.
   */
  async function requestBlob(path: string, body: unknown, options: RequestOptions = {}): Promise<Blob> {
    const response = await fetch(buildUrl(path, options.query), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...options.headers },
      body: JSON.stringify(body),
      signal: options.signal,
    });

    if (!response.ok) {
      const error = await toError(response);
      if (error.isUnauthorized && !unauthorizedNotified) {
        unauthorizedNotified = true;
        unauthorizedHandler?.();
      }
      throw error;
    }

    unauthorizedNotified = false;

    return response.blob();
  }

  return {
    get: (path, options) => request('GET', path, undefined, options),
    post: (path, body, options) => request('POST', path, body, options),
    put: (path, body, options) => request('PUT', path, body, options),
    patch: (path, body, options) => request('PATCH', path, body, options),
    delete: (path, options) => request('DELETE', path, undefined, options),
    postForBlob: (path, body, options) => requestBlob(path, body, options),
    onUnauthorized: (handler) => {
      unauthorizedHandler = handler;
    },
  };
}

/** The shared same-origin client every screen uses. */
export const api: ApiClient = createApiClient();
